import { Home, PiggyBank, TrendingUp, MapPin, HandHeart, Users, Calculator, Menu, X, FileText, BookOpen } from "lucide-react";
import { NavLink, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { Sidebar, SidebarContent, SidebarGroup, SidebarGroupContent, SidebarGroupLabel, SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from "@/components/ui/sidebar";
const mainItems = [{
  title: "Startseite",
  url: "/",
  icon: Home,
  description: "Immobilienverrentung Übersicht"
}, {
  title: "Leibrente",
  url: "/leibrente",
  icon: PiggyBank,
  description: "Monatliche Rente bis zum Lebensende"
}, {
  title: "Teilverkauf",
  url: "/teilverkauf",
  icon: TrendingUp,
  description: "Verkauf eines Teils der Immobilie"
}, {
  title: "Rückmiete",
  url: "/rueckmiete",
  icon: Home,
  description: "Verkauf mit anschließender Anmietung"
}, {
  title: "Nießbrauch",
  url: "/niessbrauch",
  icon: HandHeart,
  description: "Übertragung mit Wohn- und Nutznießrecht"
}, {
  title: "Wohnrecht",
  url: "/wohnrecht",
  icon: Users,
  description: "Grundbuchlich gesichertes Wohnrecht"
}, {
  title: "Seniorenfinanzierung",
  url: "/seniorenfinanzierung",
  icon: MapPin,
  description: "Kredit gegen Immobilie als Sicherheit"
}, {
  title: "Verrentungsgeschichten",
  url: "/erfolgsgeschichten",
  icon: BookOpen,
  description: "Echte Geschichten von Eigentümern"
}];
export function AppSidebar() {
  const {
    state,
    isMobile,
    open,
    setOpen,
    openMobile,
    setOpenMobile
  } = useSidebar();
  const location = useLocation();
  const currentPath = location.pathname;
  const isCollapsed = state === "collapsed";
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isActive = (path: string) => {
    if (path === "/" && currentPath === "/") return true;
    if (path !== "/" && currentPath.startsWith(path)) return true;
    return false;
  };

  const getNavCls = (path: string) => {
    const active = isActive(path);
    return active ? "bg-primary/10 text-primary font-medium border-r-2 border-primary" : "hover:bg-muted/50 text-muted-foreground hover:text-foreground";
  };

  // Use the appropriate state and setter based on mobile/desktop
  const isOpen = isMobile ? openMobile : open;
  const setIsOpen = isMobile ? setOpenMobile : setOpen;

  return (
    <Sidebar className={`${isMobile ? "fixed inset-y-0 left-0 z-50 w-72 bg-background border-r transform transition-transform duration-300 ease-in-out lg:relative lg:translate-x-0" : isCollapsed ? "w-16" : "w-72"}`} collapsible={isMobile ? "offcanvas" : "icon"}>
      <SidebarContent className="bg-background border-r h-full flex flex-col">
        {/* Mobile Header with Close Button */}
        {isMobile && (
          <div className="flex items-center justify-between p-4 border-b lg:hidden">
            <h2 className="text-lg md:text-xl lg:text-2xl font-bold text-primary">
              EIGENHEIMVERRENTUNG
            </h2>
            <button 
              onClick={() => setIsOpen(false)} 
              className="p-2 hover:bg-muted rounded-lg transition-colors" 
              aria-label="Menü schließen"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        )}

        {/* Navigation */}
        <SidebarGroup className="px-4 flex-1">
          <SidebarGroupLabel className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
            {(!isCollapsed || isMobile) && "Verrentungsmodelle"}
          </SidebarGroupLabel>
          
          <SidebarGroupContent>
            <SidebarMenu className="space-y-1">
              {mainItems.map(item => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild className="h-auto p-0">
                     <NavLink 
                       to={item.url} 
                       end={item.url === "/"} 
                       className={`${getNavCls(item.url)} flex items-center justify-center gap-3 p-3 rounded-lg transition-all duration-200 w-full`} 
                       onClick={() => isMobile && setIsOpen(false)}
                     >
                      <item.icon className={`h-5 w-5 flex-shrink-0 ${isActive(item.url) ? 'text-primary' : ''}`} />
                      {(!isCollapsed || isMobile) && (
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">
                            {item.title}
                          </div>
                          <div className="text-xs text-muted-foreground truncate">
                            {item.description}
                          </div>
                        </div>
                      )}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Call to Action */}
        {(!isCollapsed || isMobile) && (
          <div className="mt-auto p-6 border-t space-y-4">
            {/* Legal Links */}
            <div className="flex gap-4 text-xs">
              <NavLink 
                to="/impressum" 
                className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors" 
                onClick={() => isMobile && setIsOpen(false)}
              >
                <FileText className="h-3 w-3" />
                Impressum
              </NavLink>
              <NavLink 
                to="/datenschutz" 
                className="text-muted-foreground hover:text-primary transition-colors" 
                onClick={() => isMobile && setIsOpen(false)}
              >
                Datenschutz
              </NavLink>
              <NavLink 
                to="/agb" 
                className="text-muted-foreground hover:text-primary transition-colors" 
                onClick={() => isMobile && setIsOpen(false)}
              >
                AGB
              </NavLink>
            </div>
            
            <div className="bg-primary/5 p-4 rounded-lg">
              <h3 className="font-semibold text-primary mb-2 text-sm">
                Kostenlose Beratung
              </h3>
              <p className="text-xs text-muted-foreground mb-3">
                Finden Sie das passende Verrentungsmodell für Ihre Situation.
              </p>
              <button 
                onClick={() => {
                  window.open("https://calendly.com/renteplusimmobilie/20min", "_blank");
                  isMobile && setIsOpen(false);
                }} 
                className="w-full bg-primary text-primary-foreground text-xs px-3 py-2 rounded-md hover:bg-primary/90 transition-colors"
              >
                Jetzt beraten lassen
              </button>
            </div>
          </div>
        )}
      </SidebarContent>
    </Sidebar>
  );
}